import React from "react";
import * as Immutable from "immutable";
import trim from "lodash/trim";
import { Trans } from "react-i18next";
import i18n from "i18n";
import { Panel, BootstrapModalForm, Input } from "components/bootstrap";
import Icon from "components/common/Icon";
import ClipboardButton from "components/common/ClipboardButton";
import * as FormsUtils from "util/FormsUtils";
import QueryEditModeContext from "views/components/contexts/QueryEditModeContext";
import ParameterDataTypesSelect from "enterprise/parameters/components/ParameterDataTypesSelect";
import Portal from "components/common/Portal";
import TypeSpecificDeclarationFields from "./TypeSpecificDeclarationFields";
const IdentifierRegex = /^[\w]+$/;
const t = (key, options = {}) => i18n.t(key, { ns: "enterprise_common", ...options });
const backdropForMode = (mode) => {
  switch (mode) {
    case "query":
      return true;
    case "widget":
      return false;
    default:
      throw new Error(`Invalid query edit mode: ${mode}`);
  }
};
export default class ParameterDeclarationForm extends React.Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.handleFormSubmit = (e) => {
      const { onSave } = this.props;
      const { parameters } = this.state;
      e.preventDefault();
      e.stopPropagation();
      const newParameters = Immutable.Map(parameters.map((p) => [p.name, p]));
      onSave(newParameters).finally(() => {
        if (this._isMounted) {
        }
      });
    };
    this._validateName = (name) => {
      if (!trim(name)) {
        return ["error", t("parameters.declaration_form.must_not_be_empty")];
      }
      if (!IdentifierRegex.test(name)) {
        return ["error", t("parameters.declaration_form.only_identifier_characters")];
      }
      const { existingParameters } = this.props;
      if (existingParameters && existingParameters.has(name)) {
        return ["error", t("parameters.declaration_form.name_exists")];
      }
      return ["success"];
    };
    this._validateTitle = (title) => {
      if (!trim(title)) {
        return ["error", t("parameters.declaration_form.must_not_be_empty")];
      }
      const { existingParameters } = this.props;
      if (existingParameters && existingParameters.find(({ title: existingTitle }) => trim(title) === trim(existingTitle))) {
        return ["error", t("parameters.declaration_form.title_in_use")];
      }
      return ["success"];
    };
    this._validateParameter = (p) => ({
      name: this._validateName(p.name),
      title: this._validateTitle(p.title)
    });
    this._validateParameters = (parameters) => parameters.map((p) => this._validateParameter(p)).toMap();
    this._validate = (parameters) => {
      const newValidationStates = this._validateParameters(parameters);
      this.setState(({ validationStates: currentValidationStates }) => ({
        validationStates: currentValidationStates.mergeDeepWith(
          (state1, state2) => ({ ...state1, ...state2 }),
          newValidationStates
        )
      }));
    };
    this.handleValidate = (idx, state) => this.setState(({ validationStates }) => ({
      validationStates: validationStates.update(idx, (current) => ({ ...current, ...state }))
    }));
    this.updateParameter = (idx, updater) => {
      const { parameters } = this.state;
      const newParameters = parameters.update(idx, updater);
      this._validate(newParameters);
      this.setState({ parameters: newParameters });
    };
    this.handleParameterField = (idx) => (e) => {
      const key = e.target.name;
      const value = FormsUtils.getValueFromInput(e.target);
      this.updateParameterField(idx, key, value);
    };
    this.updateParameterField = (idx, key, value) => this.updateParameter(idx, (parameter) => parameter.toBuilder()[key](value).build());
    const { parameters } = props;
    this.state = {
      parameters: Immutable.List(parameters.valueSeq()),
      validationStates: this._validateParameters(parameters.valueSeq())
    };
  }
  componentDidMount() {
    this._isMounted = true;
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  renderParameter(parameter, idx) {
    var _a, _b, _c, _d, _e, _f;
    const { name, title, description, type } = parameter;
    const parameterSyntax = `$${name}$`;
    const { validationStates } = this.state;
    const { allowEditingName } = this.props;
    const validationState = validationStates.get(idx);
    const onChangeEvent = this.handleParameterField(idx);
    const onChange = (key, value) => this.updateParameterField(idx, key, value);
    const onChangeParameter = (updater) => this.updateParameter(idx, updater);
    return /* @__PURE__ */ React.createElement("fieldset", { key: `fieldset-${idx}` }, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: `name-${idx}`,
        type: "text",
        name: "name",
        label: t("parameters.declaration_form.name"),
        disabled: !allowEditingName,
        value: name,
        onChange: onChangeEvent,
        bsStyle: (_a = validationState == null ? void 0 : validationState.name) == null ? void 0 : _a[0],
        help: (_b = validationState == null ? void 0 : validationState.name) == null ? void 0 : _b[1],
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: `title-${idx}`,
        type: "text",
        name: "title",
        label: t("parameters.declaration_form.title_label"),
        value: title,
        onChange: onChangeEvent,
        bsStyle: (_c = validationState == null ? void 0 : validationState.title) == null ? void 0 : _c[0],
        help: (_d = validationState == null ? void 0 : validationState.title) == null ? void 0 : _d[1],
        required: true
      }
    ), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: `description-${idx}`,
        type: "text",
        name: "description",
        label: t("parameters.declaration_form.description"),
        value: description,
        bsStyle: (_e = validationState == null ? void 0 : validationState.description) == null ? void 0 : _e[0],
        help: (_f = validationState == null ? void 0 : validationState.description) == null ? void 0 : _f[1],
        onChange: onChangeEvent
      }
    ), /* @__PURE__ */ React.createElement(
      ParameterDataTypesSelect,
      {
        idx,
        validationState,
        value: type,
        onChange: onChangeParameter
      }
    ), /* @__PURE__ */ React.createElement(
      TypeSpecificDeclarationFields,
      {
        idx,
        type: type != null ? type : "value-parameter-v1",
        parameter,
        onChange,
        validationState,
        onValidate: this.handleValidate
      }
    ), /* @__PURE__ */ React.createElement(Panel, { style: { marginTop: 20 } }, /* @__PURE__ */ React.createElement(Panel.Heading, null, /* @__PURE__ */ React.createElement(Panel.Title, null, t("parameters.declaration_form.how_to_use"))), /* @__PURE__ */ React.createElement(Panel.Body, null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        ns: "enterprise_common",
        i18nKey: "parameters.declaration_form.how_to_use_text",
        components: {
          syntax: /* @__PURE__ */ React.createElement("span", { style: { whiteSpace: "nowrap" } }, /* @__PURE__ */ React.createElement("code", null, parameterSyntax), /* @__PURE__ */ React.createElement(
            ClipboardButton,
            {
              title: /* @__PURE__ */ React.createElement(Icon, { name: "content_copy" }),
              bsSize: "xsmall",
              text: parameterSyntax,
              buttonTitle: t("parameters.declaration_form.copy_to_clipboard")
            }
          ))
        }
      }
    ))));
  }
  render() {
    const { parameters, validationStates } = this.state;
    const { title = t("parameters.declare"), onClose, show } = this.props;
    const errorStates = validationStates.filter(
      (states) => Object.values(states).find((state) => state[0] === "error") !== void 0
    );
    const submitButtonDisabled = errorStates !== void 0 && !errorStates.isEmpty();
    const parameterFields = parameters.map((parameter, idx) => this.renderParameter(parameter, idx)).valueSeq().toJS();
    const backdrop = backdropForMode(this.context);
    return /* @__PURE__ */ React.createElement(Portal, null, /* @__PURE__ */ React.createElement(
      BootstrapModalForm,
      {
        submitButtonDisabled,
        submitButtonText: t("parameters.declare"),
        title,
        "data-telemetry-title": "Declare parameters",
        onCancel: onClose,
        show,
        backdrop,
        onSubmitForm: this.handleFormSubmit
      },
      parameterFields
    ));
  }
}
ParameterDeclarationForm.contextType = QueryEditModeContext;
ParameterDeclarationForm.defaultProps = {
  allowEditingName: false,
  title: void 0,
  show: true
};
